import React from "react";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import useCollectionApi from "hooks/api/private/useCollectionApi";
import { useCollectionId } from "hooks/useId";
import useSnackbar from "hooks/useSnackbar";

import { CollectionBody } from "types/api/collection";

export type FormCollectionHookType = {
    buttonSave: () => void;
    buttonUpdate: () => void;
    formHook: FormHookType<CollectionBody>;
};

function useCollectionForm() {
    const collectionId = useCollectionId();

    const { enqueueSuccessSnackbarThenNavigate, enqueueErrorSnackbar, enqueueFormErrorSnackbar } =
        useSnackbar();

    const { updateCollection, createCollection, getOneCollection } = useCollectionApi();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                name: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
            },
        };
    }, []);

    const formHook = useForm<CollectionBody>(schema);

    const { showAllError, setValue, isValid, form } = formHook;

    const collectionBody: CollectionBody = React.useMemo(() => {
        return { name: form.name };
    }, [form]);

    const buttonSave = React.useCallback(() => {
        if (isValid) {
            const [apiCall] = createCollection(collectionBody);

            apiCall
                .then(() =>
                    enqueueSuccessSnackbarThenNavigate(
                        "Collezione creata con successo",
                        "/gestione-prodotti/collezioni"
                    )
                )
                .catch(() => enqueueErrorSnackbar("Errore nella creazione della collezione"));
        } else {
            showAllError();
            enqueueFormErrorSnackbar();
        }
    }, [
        enqueueSuccessSnackbarThenNavigate,
        enqueueFormErrorSnackbar,
        enqueueErrorSnackbar,
        createCollection,
        showAllError,
        collectionBody,
        isValid,
    ]);

    const buttonUpdate = React.useCallback(() => {
        if (isValid && collectionId) {
            const [apiCall, abortController] = updateCollection(
                { id: collectionId },
                collectionBody
            );

            apiCall
                .then(() =>
                    enqueueSuccessSnackbarThenNavigate(
                        "Collezione modificata con successo",
                        "/gestione-prodotti/collezioni"
                    )
                )
                .catch(() => enqueueErrorSnackbar("Errore nella modifica della collezione"));

            return abortController;
        } else {
            showAllError();
            enqueueFormErrorSnackbar();
        }
    }, [
        enqueueSuccessSnackbarThenNavigate,
        enqueueFormErrorSnackbar,
        enqueueErrorSnackbar,
        updateCollection,
        showAllError,
        collectionId,
        collectionBody,
        isValid,
    ]);

    const [collectionDetails, setCollectionDetails] = React.useState<CollectionBody | undefined>(
        undefined
    );

    React.useEffect(() => {
        if (collectionId) {
            const [apiCall, abortController] = getOneCollection({ id: collectionId });

            apiCall.then(setCollectionDetails);

            return () => abortController.abort();
        }
    }, [getOneCollection, collectionId]);

    React.useEffect(() => {
        if (collectionDetails) {
            setValue("name", collectionDetails.name);
        }
    }, [setValue, collectionDetails]);

    return React.useMemo(
        (): FormCollectionHookType => ({ buttonSave, buttonUpdate, formHook }),
        [buttonSave, buttonUpdate, formHook]
    );
}

export default useCollectionForm;
